import { useSearchParams } from "react-router-dom";
import {
  EventType,
  Recordable,
  RecordablesAiAnalysisResponse,
  RecordablesAiReviewStatusResponse,
  Status,
  TaskItem
} from "./types";
import { useEffect, useState } from "react";
import axios from "axios";
import OrderAndAddress from "../shared/orderInfo/OrderAndAddress";
import TaskBreadcrumbs from "./TaskBreadcrumbs";
import {
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import OrderInfo from "./OrderInfo";
import TaskFilters from "./TaskFilters";
import Fuse from "fuse.js";
import { getStatusColor, getLatestEvent } from "./utils/recordableUtils";
import TaskHistory from "./TaskHistory";
import VersionUpload from "./VersionUpload";
import TaskActions from "./TaskActions";
import OrderSidebar from "./OrderSidebar";
import { CustomAvatar } from "../components/custom-avatar";
import NotesModal from "./notes/NotesModal";
import { fMonthDayYearDotNotation } from "../utils/formatTime";
import StickyTable from "../shared/StickyTable";
import RecordablesAiDrawer from "./RecordablesAiDrawer";
import Iconify from "../../minimals/components/iconify";

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const recordableId = searchParams.get("recordable-id");

  const [recordable, setRecordable] = useState<Recordable | null>(null);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [selectedTaskItem, setSelectedTaskItem] = useState<TaskItem | null>(null);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTaskType, setSelectedTaskType] = useState<number>();
  const [selectedTaskHistory, setSelectedTaskHistory] = useState<TaskItem>();
  const [openVersionUpload, setOpenVersionUpload] = useState<boolean>(false);
  const [showTaskFilters, setShowTaskFilters] = useState<boolean>(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string>();
  const [aiAnalysis, setAiAnalysis] = useState<RecordablesAiAnalysisResponse | null>(null);
  const [aiReviewStatus, setAiReviewStatus] = useState<RecordablesAiReviewStatusResponse | null>(null);
  const order = searchParams.get("order");
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

  const getAndSetStatuses = async () => {
    const { data } = await axios.get(`/api/recordablestracking/taskItems/getStatusTypes`);
    setStatuses(data);
  };

  const getAndSetEventTypes = async () => {
    const { data } = await axios.get(`/api/recordablestracking/taskItems/getEventTypes`);
    setEventTypes(data);
  };

  const getAndSetRecordable = async () => {
    const { data } = await axios.get(`/api/recordablestracking/recordables/getById?id=${recordableId}`);
    setRecordable(data);
  };

  const getAndSetLatestAiAnalysis = async () => {
    if (!recordableId) return;

    try {
      const { data, status } = await axios.get<RecordablesAiAnalysisResponse | "">(
        `/api/recordablesai/latest?recordableId=${recordableId}`
      );

      if (status === 200 && data) {
        setAiAnalysis(data as RecordablesAiAnalysisResponse);
      }
    } catch {
      // Saved AI reviews may not exist yet while the feature/table is being rolled out.
    }
  };

  const getAndSetAiReviewStatus = async (ensure = false) => {
    if (!recordableId) return;

    if (ensure) {
      setAiReviewStatus((current) =>
        current ?? {
          status: "Pending",
          hasReview: false,
          hasAiTasks: true,
          message: null
        }
      );
    }

    try {
      const endpoint = ensure ? "ensure" : "status";
      const response = ensure
        ? await axios.post<RecordablesAiReviewStatusResponse>(
            `/api/recordablesai/${endpoint}?recordableId=${recordableId}`
          )
        : await axios.get<RecordablesAiReviewStatusResponse>(
            `/api/recordablesai/${endpoint}?recordableId=${recordableId}`
          );
      setAiReviewStatus(response.data);
      if (response.data.status === "Completed") {
        await getAndSetLatestAiAnalysis();
      }
    } catch {
      setAiReviewStatus({
        status: "Failed",
        hasReview: false,
        hasAiTasks: true,
        message: "The AI review status could not be loaded."
      });
    }
  };

  const updateStatus = async (taskId: number, statusId: number) => {
    await axios.post(`/api/recordablestracking/taskItems/updateStatus`, {
      id: taskId,
      status: statusId
    });
    updateData();
  };

  useEffect(() => {
    if (recordableId) {
      getAndSetStatuses();
      getAndSetEventTypes();
      getAndSetRecordable();
      getAndSetLatestAiAnalysis();
      getAndSetAiReviewStatus(true);
    }
  }, [recordableId]);

  useEffect(() => {
    if (!recordableId || !aiReviewStatus || !["Pending", "Processing"].includes(aiReviewStatus.status)) {
      return;
    }

    const interval = window.setInterval(() => getAndSetAiReviewStatus(), 3000);
    return () => window.clearInterval(interval);
  }, [recordableId, aiReviewStatus?.status]);

  const toggleShowTaskFilters = () => setShowTaskFilters(!showTaskFilters);

  const filterTasks = () => {
    const fuseOptions = {
      keys: ["description"], // Specify the properties to search on
      threshold: 0.5 // Set the threshold for fuzzy matching (0.0 to 1.0)
    };
    const filteredTasksByStatus =
      selectedTaskType !== undefined
        ? recordable?.taskItems.filter((t) => t.status === selectedTaskType) || []
        : recordable?.taskItems || [];
    const fuse = new Fuse(filteredTasksByStatus, fuseOptions);
    return searchTerm === "" ? filteredTasksByStatus : fuse.search(searchTerm).map((f) => f.item);
  };

  const filteredTasks = filterTasks();
  const recordableTypeDescription = recordable?.typeDescription || "";

  const updateData = () => {
    getAndSetRecordable();
  };

  const analyzeWithAi = async () => {
    if (!recordableId) return;

    setAiDrawerOpen(true);

    if (aiReviewStatus?.status === "Completed") {
      await getAndSetLatestAiAnalysis();
      return;
    }

    if (aiReviewStatus?.status === "Pending" || aiReviewStatus?.status === "Processing") {
      return;
    }

    if (!order) return;

    setAiLoading(true);
    setAiError(undefined);

    try {
      const { data: orderInfo } = await axios.get(`/api/recordablestracking/orderInfo/getinfo?orderNumber=${order}`);
      const { data } = await axios.post<RecordablesAiAnalysisResponse>("/api/recordablesai/analyze", {
        recordableId: Number(recordableId),
        orderNumber: order,
        orderInfo
      });
      setAiAnalysis(data);
      setAiReviewStatus({
        status: "Completed",
        hasReview: true,
        hasAiTasks: true,
        message: null
      });
    } catch (error: any) {
      setAiError(error?.response?.data?.message || "AI analysis failed. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiTaskAction = async (taskItemId: number, status: number) => {
    if (!recordableId || !aiAnalysis) return;

    try {
      const { data } = await axios.post<{ taskAction: RecordablesAiAnalysisResponse["taskActions"][number] }>(
        "/api/recordablesai/task-action",
        {
          analysisId: aiAnalysis.analysisId,
          recordableId: Number(recordableId),
          taskItemId,
          status
        }
      );

      setAiAnalysis((current) =>
        current
          ? {
              ...current,
              taskActions: [
                ...(current.taskActions ?? []).filter((action) => action.taskItemId !== taskItemId),
                data.taskAction
              ]
            }
          : current
      );
      await getAndSetRecordable();
    } catch (error: any) {
      setAiError(error?.response?.data?.message || "The AI task status could not be saved.");
    }
  };

  const currentDocumentPdfUrl = recordable?.documents?.[0]?.momentumDocumentId
    ? `/opendoc?id=${recordable.documents[0].momentumDocumentId}`
    : undefined;

  return (
    <>
      <OrderAndAddress />
      <TaskBreadcrumbs recordableType={recordableTypeDescription} />
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <TableContainer component={Card}>
            {recordable && (
              <OrderInfo
                recordable={recordable}
                tasks={tasks}
                setOpenVersionUpload={setOpenVersionUpload}
                showTaskFilters={showTaskFilters}
                toggleShowTaskFilters={toggleShowTaskFilters}
                analyzeWithAi={analyzeWithAi}
                isAnalyzingWithAi={aiLoading}
                aiReviewStatus={aiReviewStatus}
              />
            )}
            <Divider />
            {showTaskFilters && (
              <TaskFilters options={statuses} setSearchTerm={setSearchTerm} setSelectedTaskType={setSelectedTaskType} />
            )}
            <StickyTable>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Tasks To Do</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Latest Activity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map((t) => {
                  var latestEvent = getLatestEvent(t);
                  var userNotesCount = t.notes.filter((n) => n.isUserNote).length;
                  return (
                    <TableRow key={t.id}>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell width={500}>
                        <Typography variant="subtitle2">{t.description}</Typography>
                      </TableCell>
                      <TableCell width={500}>
                        {t.assignedToName && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CustomAvatar name={t.assignedToName || ""} />
                            <Box>
                              <Typography typography="subtitle2">{t.assignedToName}</Typography>
                              <Typography typography="body2">{t.assignedToEmail}</Typography>
                            </Box>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell width={500}>
                        {latestEvent && (
                          <>
                            <Typography variant="body2" color="GrayText" noWrap>
                              <Typography
                                variant="body2"
                                display="inline-block"
                                color="info.main"
                                onClick={() => setSelectedTaskHistory(t)}
                                sx={{ cursor: "pointer" }}
                              >
                                {eventTypes.find((t) => t.id === latestEvent?.eventType)?.description}
                              </Typography>{" "}
                              on {latestEvent?.dateTime && fMonthDayYearDotNotation(latestEvent?.dateTime)}
                            </Typography>
                            <Typography variant="body2" color="GrayText">
                              by&nbsp;{latestEvent?.username}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" color={getStatusColor(t.status)} noWrap>
                          {statuses.find((s) => s.id === t.status)?.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TaskActions
                          updateStatus={(statusId: number) => updateStatus(t.id, statusId)}
                          viewTaskHistory={() => setSelectedTaskHistory(t)}
                          addNoteAction={() => setSelectedTaskItem(t)}
                          badgeCount={userNotesCount}
                        />
                      </TableCell>
                      <TableCell width={400} />
                    </TableRow>
                  );
                })}
              </TableBody>
            </StickyTable>
          </TableContainer>
        </Grid>
        <Grid item xs={3}>
          <OrderSidebar />
        </Grid>
      </Grid>
      {selectedTaskHistory && (
        <TaskHistory
          task={selectedTaskHistory}
          recordableType={recordableTypeDescription}
          recordableDescription={recordable?.description}
          documents={recordable?.documents || []}
          statuses={statuses}
          eventTypes={eventTypes}
          onClose={() => setSelectedTaskHistory(undefined)}
        />
      )}
      {openVersionUpload && (
        <VersionUpload
          open={openVersionUpload}
          onClose={() => setOpenVersionUpload(false)}
          updateData={updateData}
          recordableId={recordableId}
          documentType={recordableTypeDescription}
          additionalDescription={recordable?.description}
          newVersionCount={recordable?.documents?.length ? recordable?.documents?.length + 1 : 0}
        />
      )}
      {selectedTaskItem && (
        <NotesModal
          selectedTaskItem={selectedTaskItem}
          setSelectedTaskItem={setSelectedTaskItem}
          updateTasks={getAndSetRecordable}
        />
      )}
      {aiAnalysis && !aiDrawerOpen && (
        <IconButton
          onClick={() => setAiDrawerOpen(true)}
          color="primary"
          sx={{
            position: "fixed",
            top: 116,
            right: 0,
            zIndex: 1200,
            width: 34,
            height: 118,
            borderRadius: "8px 0 0 8px",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.22)",
            "&:hover": {
              backgroundColor: "primary.dark"
            }
          }}
        >
          <Stack spacing={0.75} alignItems="center">
            <Iconify icon="eva:flash-fill" />
            <Typography
              variant="caption"
              sx={{
                color: "inherit",
                fontWeight: 900,
                lineHeight: 1,
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textTransform: "uppercase"
              }}
            >
              AI Review
            </Typography>
          </Stack>
        </IconButton>
      )}
      <RecordablesAiDrawer
        open={aiDrawerOpen}
        loading={aiLoading || aiReviewStatus?.status === "Pending" || aiReviewStatus?.status === "Processing"}
        error={aiError}
        analysis={aiAnalysis}
        pdfUrl={currentDocumentPdfUrl}
        onClose={() => setAiDrawerOpen(false)}
        onTaskAction={applyAiTaskAction}
      />
    </>
  );
}
